import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Card, CardActionArea } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { TIPS_CATEGORIES, TipCategory, getTipsByCategory } from './type';

const CATEGORY_COLORS: Record<TipCategory, string> = {
    methode: '#2B4FA8',
    'paris-hippiques': '#5A3FB0',
    strategie: '#1F7A85',
};

const TipsIndexPage: FC = () => {
    const categories = Object.keys(TIPS_CATEGORIES) as TipCategory[];

    return (
        <Box
            maxWidth={900}
            mx="auto"
            p={2}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
        >
            <Helmet>
                <title>Conseils et méthode | ExpertTurf</title>
                <meta
                    name="description"
                    content="Comprendre les paris hippiques, notre méthode d'analyse et nos conseils pour parier intelligemment."
                />
            </Helmet>

            <Typography
                variant="h3"
                component="h1"
                fontWeight={700}
                gutterBottom
                color='secondary.main'
                textAlign="left"
            >
                Conseils & méthode
            </Typography>
            <Typography
                variant="subtitle1"
                color="text.secondary"
                mb={5}
                textAlign="left"
            >
                Tout ce qu'il faut savoir pour comprendre nos pronostics et parier avec
                discernement.
            </Typography>

            {categories.map((category) => {
                const { label, description } = TIPS_CATEGORIES[category];
                const entries = getTipsByCategory(category);
                const color = CATEGORY_COLORS[category];

                return (
                    <Box key={category} mb={6} width="100%">
                        <Box display="flex" alignItems="flex-start" gap={1.5} mb={0.5}>
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <Box width={8} height={8} borderRadius="50%" bgcolor={color} flexShrink={0} />
                                <Typography variant="h5" component="h2" fontWeight={700} textAlign="left">
                                    {label}
                                </Typography>
                            </Box>
                        </Box>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={2}
                            ml={2.5}
                            textAlign="left"
                        >
                            {description}
                        </Typography>

                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-start"
                            gap={1.5}
                            width="100%"
                            pl={{ xs: 1, sm: 4 }}
                        >
                            {entries.map((entry) => (
                                <Card
                                    key={entry.slug}
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 2,
                                        borderLeft: `4px solid ${color}`,
                                        width: '100%',
                                        transition: 'box-shadow 0.2s ease, transform 0.15s ease',
                                        '&:hover': {
                                            boxShadow: '0px 6px 16px rgba(0,0,0,0.12)',
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    <CardActionArea
                                        component={Link}
                                        to={`/tips/${category}/${entry.slug}`}
                                        sx={{
                                            p: 2,
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="flex-start"
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                fontWeight={700}
                                                textAlign="left"
                                            >
                                                {entry.title}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                textAlign="left"
                                            >
                                                {entry.description}
                                            </Typography>
                                        </Box>
                                        <ArrowForwardIcon
                                            sx={{ color, fontSize: 20, flexShrink: 0, mt: 0.3 }}
                                        />
                                    </CardActionArea>
                                </Card>
                            ))}
                        </Box>
                    </Box>
                );
            })}

            <Card
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    bgcolor: 'grey.50',
                    borderLeft: '4px solid',
                    borderLeftColor: 'secondary.main',
                    width: '100%',
                    transition: 'box-shadow 0.2s ease, transform 0.15s ease',
                    '&:hover': {
                        boxShadow: '0px 6px 16px rgba(0,0,0,0.12)',
                        transform: 'translateY(-2px)',
                    },
                }}
            >
                <CardActionArea
                    component={Link}
                    to="/tips/lexique"
                    sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                >
                    <MenuBookIcon
                        sx={{
                            color: 'secondary.main',
                            fontSize: 22,
                            flexShrink: 0,
                            mt: 0.3,
                        }}
                    />
                    <Box display="flex" flexDirection="column" alignItems="flex-start">
                        <Typography variant="subtitle1" fontWeight={700} textAlign="left">
                            Lexique du trot et du galop
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="left">
                            Tous les termes techniques expliqués simplement.
                        </Typography>
                    </Box>
                </CardActionArea>
            </Card>
        </Box>
    );
};

export default TipsIndexPage;
